/**
 * Format number with intelligent precision based on magnitude
 * @param {number} value - Number to format
 * @param {number} maxDecimals - Maximum decimal places (default: 2)
 * @returns {string} Formatted number
 */
export const formatNumber = (value, maxDecimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const num = Number(value);
  
  // For zero, return '0'
  if (num === 0) {
    return '0';
  }

  // For very small numbers (< 0.01), use scientific notation or more decimals
  if (Math.abs(num) < 0.01) {
    return num.toPrecision(2);
  }

  // For numbers < 10, show up to maxDecimals
  if (Math.abs(num) < 10) {
    return num.toFixed(maxDecimals).replace(/\.?0+$/, '');
  }

  // For numbers 10-999, show 1 decimal if needed
  if (Math.abs(num) < 1000) {
    return num.toFixed(1).replace(/\.0$/, '');
  }

  // For numbers >= 1000, show no decimals
  return Math.round(num).toString();
};

/**
 * Format percentage value
 * @param {number} value - Percentage value to format
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value) => {
  return formatNumber(value, 2);
};

/**
 * Format weight in grams with appropriate precision
 * @param {number} value - Weight in grams
 * @returns {string} Formatted weight
 */
export const formatWeight = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const num = Number(value);
  
  if (num === 0) {
    return '0';
  }

  // For very small amounts, show 2 decimals
  if (Math.abs(num) < 1) {
    return num.toFixed(2).replace(/\.?0+$/, '');
  }

  // For amounts < 100, show 1 decimal
  if (Math.abs(num) < 100) {
    return num.toFixed(1).replace(/\.0$/, '');
  }

  // For larger amounts, round to integer
  return Math.round(num).toString();
};
